/**
 * User Action Tracking Utility
 *
 * Provides functionality for generating user action IDs and W3C traceparent headers
 * to enable end-to-end trace visibility across the Assistant, ai-integration, and LLM services.
 *
 * The client generates valid W3C traceparent headers which ai-integration (server-side)
 * uses to create/continue traces in Langfuse. No Langfuse keys are needed on the client.
 */

import {
	HEADER_FEATURE_NAME,
	HEADER_INTERFACE_NAME,
	HEADER_QUOTA_ID,
	HEADER_TRACEPARENT,
	HEADER_USER_ACTION_ID,
} from '@/constants';
import {
	captureConsumption,
	reportConsumption,
} from '@/services/llm/llm-consumption-service';
import { nanoidHex, nanoidUUIDv4 } from './id-utils';

export function generateUserActionId(): string {
	return nanoidUUIDv4();
}

export function generateTraceparent(): string {
	const traceId = nanoidHex(32);
	const spanId = nanoidHex(16);
	const traceFlags = '01';

	return `00-${traceId}-${spanId}-${traceFlags}`;
}

export interface UserActionHeaders extends Record<string, string> {
	[HEADER_USER_ACTION_ID]: string;
	[HEADER_TRACEPARENT]: string;
	[HEADER_INTERFACE_NAME]: string;
	[HEADER_FEATURE_NAME]: string;
	[HEADER_QUOTA_ID]: string;
}

class UserActionContext {
	private currentActionId: string | null = null;
	private currentTraceparent: string | null = null;
	private interfaceName: string | null = null;
	private featureName: string | null = null;
	private quotaId: string | null = null;

	private _ensureContext(
		interfaceName?: string,
		featureName?: string,
	): UserActionHeaders {
		this.currentActionId = generateUserActionId();
		this.currentTraceparent = generateTraceparent();
		this.interfaceName = interfaceName ?? null;
		this.featureName = featureName ?? null;
		return this._buildHeaders();
	}

	private _buildHeaders(): UserActionHeaders {
		return {
			[HEADER_USER_ACTION_ID]: this.currentActionId ?? '',
			[HEADER_TRACEPARENT]: this.currentTraceparent ?? '',
			[HEADER_INTERFACE_NAME]: this.interfaceName ?? '',
			[HEADER_FEATURE_NAME]: this.featureName ?? '',
			[HEADER_QUOTA_ID]: this.quotaId ?? '',
		};
	}

	getHeaders(): UserActionHeaders {
		if (!this.currentActionId || !this.currentTraceparent) {
			return this._ensureContext();
		}
		return this._buildHeaders();
	}

	async startAction(
		interfaceName?: string,
		featureName?: string,
	): Promise<UserActionHeaders> {
		this._ensureContext(interfaceName, featureName);
		try {
			const captureUuid = await captureConsumption(withTrackingHeaders());
			if (captureUuid) {
				this.quotaId = captureUuid;
			}
			return this._buildHeaders();
		} catch (e) {
			this.currentActionId = null;
			this.currentTraceparent = null;
			this.interfaceName = null;
			this.featureName = null;
			this.quotaId = null;
			throw e;
		}
	}

	endAction(): void {
		if (this.quotaId) {
			reportConsumption(this.quotaId, withTrackingHeaders());
		}
		this.currentActionId = null;
		this.currentTraceparent = null;
		this.interfaceName = null;
		this.featureName = null;
		this.quotaId = null;
	}
}

const userActionContext = new UserActionContext();

export const startUserAction = (interfaceName?: string, featureName?: string) =>
	userActionContext.startAction(interfaceName, featureName);

export function getUserActionHeaders(): UserActionHeaders {
	return userActionContext.getHeaders();
}

const DEFAULT_HTTP_CLIENT_HEADERS: Record<string, string> = {
	Accept: 'application/json, text/plain, */*',
	'Content-Type': 'application/json;charset=UTF-8',
};

function normalizeHeaders(
	headers: HeadersInit | undefined,
): Record<string, string> {
	if (!headers) {
		return {};
	}

	if (headers instanceof Headers) {
		const result: Record<string, string> = {};
		headers.forEach((value, key) => {
			result[key] = value;
		});
		return result;
	}

	if (Array.isArray(headers)) {
		const result: Record<string, string> = {};
		for (const [key, value] of headers) {
			result[key] = String(value);
		}
		return result;
	}

	return { ...headers };
}

export function withTrackingHeaders(options: RequestInit = {}): RequestInit {
	const trackingHeaders = getUserActionHeaders();
	const normalizedHeaders = normalizeHeaders(options.headers);
	const headers: Record<string, string> = {
		...DEFAULT_HTTP_CLIENT_HEADERS,
		...normalizedHeaders,
		...trackingHeaders,
	};
	return {
		...options,
		headers,
	};
}

export const endUserAction = () => userActionContext.endAction();

/**
 * Wraps an async function with user-action tracking (capture at start, report at end).
 * Use for standalone engine calls (e.g., from MCP Server) when tracing and consumption tracking are required.
 * @param interfaceName - e.g. 'MCP'
 * @param featureName - e.g. 'BUILD-CHART'
 * @param fn - The async function to execute
 * @returns The result of fn
 */
export async function runWithUserAction<T>(
	interfaceName: string,
	featureName: string,
	fn: () => Promise<T>,
): Promise<T> {
	let started = false;
	try {
		await startUserAction(interfaceName, featureName);
		started = true;
		return await fn();
	} finally {
		if (started) {
			endUserAction();
		}
	}
}
